import qs from 'qs'
import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { deleteDetergentHistory, getAllDetergentHistories } from '@/apis/detergentHistories'
import { API_LIMIT } from '@/constants/api'
import useNotification from '@/hooks/useNotification'
import useQuery from '@/hooks/useQuery'
import useQueryPage from '@/hooks/useQueryPage'
import { GetAllDetergentHistoryResponse } from '@/types/apis/detergentHistories/GetAllDetergentHistoryResponse'
import { GetAllDetergentHistoryRequest } from '@/types/apis/detergentHistories/GetAllDetergntHistoryRequest'
import { DeleteIdCardRequest } from '@/types/apis/idCards'

import { PageQueryParams } from '../Tank/Tank.hooks'

export const useDetergentHistories = () => {
  const { t } = useTranslation()
  const openNotification = useNotification()
  const currentPage = useQueryPage()
  const query = useQuery()
  const [page, setPage] = useState(currentPage)
  const [getDetergentHistoriesLoading, setGetDetergentHistoriesLoading] = useState(false)
  const [getDetergentHistoriesResponse, setDetergentHistoriesResponse] = useState<GetAllDetergentHistoryResponse>()
  const [deleteDetergentHistoryLoading, setDeleteDetergentHistoryLoading] = useState(false)

  const filterToQuery = (filter: { page?: number }): string => {
    const filterQuery: PageQueryParams = {}

    if (filter.page) {
      filterQuery.page = filter.page
    }

    return qs.stringify(filterQuery)
  }

  const get = useCallback(
    async (request: GetAllDetergentHistoryRequest) => {
      try {
        setGetDetergentHistoriesLoading(true)
        const res = await getAllDetergentHistories({ ...request, limit: API_LIMIT, page })

        setDetergentHistoriesResponse(res)
      } finally {
        setGetDetergentHistoriesLoading(false)
      }
    },
    [page]
  )

  const del = useCallback(
    async ({ id }: DeleteIdCardRequest) => {
      try {
        setDeleteDetergentHistoryLoading(true)
        await deleteDetergentHistory({ id })

        if (getDetergentHistoriesResponse) {
          const newDetergentHistories = getDetergentHistoriesResponse.items.filter((item) => item.id !== id)
          setDetergentHistoriesResponse({ ...getDetergentHistoriesResponse, ...newDetergentHistories })
        }
        openNotification({ type: 'success', description: t('components.pages.admins.common.delete.success') })
      } catch {
        openNotification({ type: 'error', description: t('components.pages.admins.common.delete.fail') })
      } finally {
        setDeleteDetergentHistoryLoading(false)
      }
    },
    [getDetergentHistoriesResponse, openNotification, t]
  )

  useEffect(() => {
    const page = query.get('page')
    setPage(page ? Number(page) : 1)
  }, [query])

  return {
    getDetergentHistories: get,
    getDetergentHistoriesResponse,
    getDetergentHistoriesLoading,
    deleteDetergentHistory: del,
    deleteDetergentHistoryLoading,
    page,
    pageSize: API_LIMIT,
    filterToQuery,
  }
}
